import { c as _c } from "react/compiler-runtime";
import { graphemeBudget, graphemeCount, REVEAL_SETTLE_MS, stepRevealedText } from '@src/components/sco/Chat/revealText';
import { useEffect, useRef, useState } from 'react';

/**
 * Reveal `target` toward the latest server body.
 * History, reduced motion, and a bubble that mounted with text already in it
 * return the target unchanged. A turn that is still streaming keeps unsafe
 * tails held. Any other pause flushes to the exact target so a finished
 * reply cannot stay clipped.
 */
export function useRevealedText(target, live, streamOpen) {
  const $ = _c(7);
  let t0;
  if ($[0] !== target) {
    t0 = () => target.trim().length === 0;
    $[0] = target;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [startedEmpty] = useState(t0);
  const allowAnimate = live && startedEmpty;
  const shownRef = useRef("");
  const [shown, setShown] = useState("");
  let t1;
  let t2;
  if ($[2] !== allowAnimate || $[3] !== streamOpen || $[4] !== target) {
    t1 = () => {
      if (!allowAnimate) {
        return;
      }
      let raf = 0;
      let settle = 0;
      let last = 0;
      let stopped = false;
      const commit = next => {
        if (next === shownRef.current) {
          return;
        }
        shownRef.current = next;
        setShown(next);
      };
      const tick = now => {
        if (stopped) {
          return;
        }
        const current = shownRef.current;
        if (!target.startsWith(current)) {
          commit(target);
          return;
        }
        if (current === target) {
          return;
        }
        if (last !== 0 && now - last < 32) {
          raf = requestAnimationFrame(tick);
          return;
        }
        const elapsed = Math.min(48, Math.max(32, last === 0 ? 32 : now - last));
        last = now;
        const behind = graphemeCount(target.slice(current.length));
        const next_0 = stepRevealedText(current, target, graphemeBudget(behind, elapsed), false);
        if (next_0 === current) {
          return;
        }
        commit(next_0);
        if (next_0 !== target) {
          raf = requestAnimationFrame(tick);
        }
      };
      cancelAnimationFrame(raf);
      window.clearTimeout(settle);
      raf = requestAnimationFrame(tick);
      if (!streamOpen) {
        settle = window.setTimeout(() => {
          if (!stopped) {
            commit(target);
          }
        }, REVEAL_SETTLE_MS);
      }
      return () => {
        stopped = true;
        cancelAnimationFrame(raf);
        window.clearTimeout(settle);
      };
    };
    t2 = [allowAnimate, streamOpen, target];
    $[2] = allowAnimate;
    $[3] = streamOpen;
    $[4] = target;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  if (!allowAnimate) {
    return target;
  }
  if (shown && !target.startsWith(shown)) {
    return target;
  }
  return shown;
}